import { makeStyles } from '@mui/styles';
import Grid from 'apollo-react/components/Grid';
import IconButton from 'apollo-react/components/IconButton';
import Tooltip from 'apollo-react/components/Tooltip';
import StatusNegative from 'apollo-react-icons/StatusNegative';
import React, { useMemo } from 'react';

import ColumnSelect from './components/ColumnSelect';
import GroupSelect from './components/GroupSelect';
import Operator from './components/Operator';
import RelationalOperatorSelect from './components/RelationalOperatorSelect';
import Value from './components/Value';
import { getColumnType, getFullColumnName, hasViewName } from './utils';
import { useTranslation } from '../../../custom-hooks/useTranslation';
import { useWindowDimensions } from '../../../custom-hooks/useWindowDimensions';
import { getUITA } from '../../../utils/commonUtils';
import { isNilOrEmpty } from '../../../utils/ramdaUtils';

const useStyles = makeStyles({
  rootMobile: {
    marginTop: 25,
  },
  deleteContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  deleteContainerMobile: {
    display: 'flex',
    width: '100%',
  },
});

const Criterion = ({
  openGroup,
  closeGroup,
  columns,
  column,
  columnType,
  viewName,
  value,
  operator,
  lookupValues,
  relationalOperator,
  onCriterionChange,
  onLookup,
  allowEditting,
  enableLookup,
  isOutPopover = false,
  onDelete,
  isFirstRow,
}) => {
  const classes = useStyles();
  const translateString = useTranslation();

  const { isMobileDevice } = useWindowDimensions();

  const hideValueField = ['ISNULL', 'ISNOTNULL'].includes(operator);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columnSelectValue = useMemo(() => (hasViewName(column) ? column : ''), [column]);
  const colType = useMemo(
    () =>
      !columnType && columns?.length > 0
        ? getColumnType(getFullColumnName(viewName, column), columns)
        : columnType,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columns, columnType]
  );

  const handleChange = (field) => (val) => {
    onCriterionChange({ [field]: val });
  };

  const handleColumnChange = (column) => {
    onCriterionChange({
      column,
      columnType: getColumnType(column, columns),
    });
  };

  const handleLookup = () => {
    onLookup(getFullColumnName(viewName, column));
  };

  return (
    <Grid container spacing={2} className={isMobileDevice ? classes.rootMobile : null}>
      <Grid item>
        <GroupSelect
          groupType="("
          value={openGroup}
          onChange={handleChange('openGroup')}
          disabled={!allowEditting}
          ui-ta={getUITA('FormCtrl SSDropDown GroupSelectOpen')}
          showLabel={isFirstRow || isMobileDevice}
        />
      </Grid>
      <Grid item>
        <ColumnSelect
          columns={columns}
          value={columnSelectValue}
          onChange={handleColumnChange}
          disabled={!allowEditting}
          ui-ta={getUITA('FormCtrl SSDropDown ColumnSelect')}
          showLabel={isFirstRow || isMobileDevice}
        />
      </Grid>
      <Grid item>
        <Operator
          value={operator}
          column={column}
          type={colType}
          onChange={handleChange('operator')}
          disabled={!allowEditting || isNilOrEmpty(column)}
          ui-ta={getUITA('FormCtrl SSDropDown Operator')}
          showLabel={isFirstRow || isMobileDevice}
        />
      </Grid>
      <Grid item>
        <Value
          value={value}
          type={columnType}
          onChange={handleChange('value')}
          disabled={!allowEditting || isNilOrEmpty(column)}
          hidden={hideValueField}
          lookupValues={lookupValues}
          onLookup={handleLookup}
          enableLookup={enableLookup}
          isOutPopover={isOutPopover}
          showLabel={isFirstRow || isMobileDevice}
        />
      </Grid>
      <Grid item>
        <GroupSelect
          groupType=")"
          value={closeGroup}
          onChange={handleChange('closeGroup')}
          disabled={!allowEditting}
          ui-ta={getUITA('FormCtrl SSDropDown GroupSelectClose')}
          showLabel={isFirstRow || isMobileDevice}
        />
      </Grid>
      <Grid item>
        <RelationalOperatorSelect
          value={relationalOperator}
          onChange={handleChange('relationalOperator')}
          disabled={!allowEditting}
          ui-ta={getUITA('FormCtrl SSDropDown RelationalOperatorSelect')}
          showLabel={isFirstRow || isMobileDevice}
        />
      </Grid>
      <Grid
        item
        className={isMobileDevice ? classes.deleteContainerMobile : classes.deleteContainer}
      >
        <Tooltip title={!allowEditting ? null : translateString('Delete')} placement="top">
          <IconButton
            style={{ marginRight: 5, marginTop: isFirstRow ? '15px' : 0 }}
            size="small"
            disabled={!allowEditting}
            onClick={onDelete}
            aria-label={translateString('Delete')}
            ui-ta={getUITA('AdvancedSearch DeleteButton')}
          >
            <StatusNegative fontSize="small" />
          </IconButton>
        </Tooltip>
      </Grid>
    </Grid>
  );
};

export default Criterion;
